import { Form, FormGroup, FormFeedback, Alert, Spinner } from "reactstrap";
import { trackMetaEvent } from "../common/analytics";
import { AuthButton, AuthContainer, AuthInput } from "./style";
import Link from "../common/Link";
import { useState, useContext, useCallback, useEffect } from "react";
import { register, fetchOtp, setAuthenticatedUser } from "./service";
import { CoreContainer } from "../common/style";
import { AppContext } from "../context";
import { useNavigate } from "react-router-dom";
import OtpVerification from "./OtpVerification";

const profileOptions = {
  home_owner: "Home Owner / Self",
  architect_designer: "Architect / Designer",
  builder_contrator: "Builder / Contractor",
  module_factory: "Modular Factory",
  carpenter: "Carpenter",
};

function Register() {
  const navigate = useNavigate();
  const { setOrganizationData } = useContext(AppContext);
  const [step, setStep] = useState(1);

  const [profile, setProfile] = useState(null);
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [buildMode, setBuildMode] = useState("");
  const [monthlyOrders, setMonthlyOrders] = useState("");
  const [size, setSize] = useState("");

  const [timer, setTimer] = useState(0);
  const timeOutCallback = useCallback(
    () => setTimer((currTimer) => currTimer - 1),
    []
  );
  useEffect(() => {
    timer > 0 && setTimeout(timeOutCallback, 1000);
  }, [timer, timeOutCallback]);

  useEffect(() => {
    trackMetaEvent("startRegistration");
  }, []);

  useEffect(() => {
    if (showOtpVerification) {
      setError(null);
    }
  }, [showOtpVerification]);
  const enableRegister =
    fullName.trim().length > 1 &&
    companyName.trim().length > 1 &&
    mobileNumber.length === 10 &&
    /^\d+$/.test(mobileNumber) &&
    buildMode !== "" &&
    size !== "";

  function resetTimer() {
    if (!timer) setTimer(30);
  }

  function triggerGetOtp() {
    resetTimer();
    fetchOtp({ mobileNumber, from: "register" })
      .then(() => setShowOtpVerification(true))
      .catch(({ error }) => setError(error))
      .finally(() => setIsLoading(false));
  }

  function registerUser(fullOtp) {
    setIsLoading(true);
    register({
      fullName,
      companyName,
      mobileNumber,
      profile,
      size,
      buildMode,
      otp: fullOtp,
    })
      .then(({ token, user, plan_features }) => {
        trackMetaEvent("completeRegistration");
        setIsRegistered(true);
        setAuthenticatedUser({ token, user });
        setOrganizationData(plan_features);
        navigate("/projects", { state: { setIdentity: true } });
      })
      .catch(({ error }) => setError(error))
      .finally(() => setIsLoading(false));
  }

  if (showOtpVerification) {
    return (
      <OtpVerification
        mobileNumber={mobileNumber}
        setShowOtpVerification={setShowOtpVerification}
        onSuccess={registerUser}
        error={error}
        setError={setError}
        isLoading={isLoading}
        triggerGetOtp={triggerGetOtp}
      />
    );
  }

  return (
    <CoreContainer>
      <AuthContainer className="px-4 px-sm-5 pt-4 pb-2 rounded-3">
        <Form>
          {/* Step 1: Profile Selection */}
          {step === 1 && (
            <>
              <h4 className="text-center mt-3 fw-bold">Create Your Account</h4>
              <p className="text-center text-muted mb-4">
                Tell us about your profile to get started
              </p>

              <FormGroup className="mt-4">
                <label className="fw-semibold mb-3">
                  What is your Profile?
                </label>
                <div className="d-flex flex-column gap-3">
                  {Object.entries(profileOptions).map(([key, label]) => (
                    <label
                      key={key}
                      htmlFor={key}
                      className={`border rounded-3 px-3 py-2 d-flex align-items-center justify-content-between ${
                        profile === key
                          ? "border-primary bg-light"
                          : "border-secondary-subtle"
                      }`}
                      style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                    >
                      <span className="fw-medium">{label}</span>
                      <input
                        type="radio"
                        id={key}
                        className="form-check-input"
                        name="profile"
                        checked={profile === key}
                        onChange={() => setProfile(key)}
                      />
                    </label>
                  ))}
                </div>
              </FormGroup>

              <div className="d-grid gap-2 mt-4 mb-3 pt-3">
                <AuthButton
                  color="primary"
                  onClick={() => {
                    if (profile === "home_owner" || profile === "carpenter") {
                      setStep("thankyou");
                    } else {
                      setStep(2);
                    }
                  }}
                  disabled={!profile}
                >
                  Continue
                </AuthButton>
              </div>
              <p className="text-center">
                Have already an account? &nbsp;
                <Link
                  to="/login_with_otp"
                  children="Login Here"
                  fontWeight="700"
                  color="black"
                />
              </p>
            </>
          )}

          {/* Step 2: Form Inputs */}
          {step === 2 && (
            <>
              <div onClick={() => setStep(1)} className="pointer mb-2 text-decoration-underline">
                <i className="fas fa-chevron-left fs-6"/>
                <span className="ms-1 fs-6">Back</span>
              </div>

              <h4 className="text-center mt-3 fw-bold">Create Your Account</h4>

              <FormGroup className="mt-4">
                <AuthInput
                  type="text"
                  invalid={fullName.length > 0 && fullName.trim().length < 2}
                  placeholder="Your Name"
                  value={fullName}
                  onChange={(ev) => setFullName(ev.target.value)}
                />
                <FormFeedback>
                  Name must contain at least 2 characters.
                </FormFeedback>
              </FormGroup>

              <FormGroup className="mt-4">
                <AuthInput
                  type="text"
                  invalid={
                    companyName.length > 0 && companyName.trim().length < 2
                  }
                  placeholder="Company Name"
                  value={companyName}
                  onChange={(ev) => setCompanyName(ev.target.value)}
                />
                <FormFeedback>
                  Company Name must contain at least 2 characters.
                </FormFeedback>
              </FormGroup>

              <FormGroup className="mt-4">
                <AuthInput
                  type="tel"
                  invalid={
                    mobileNumber.length > 0 &&
                    (mobileNumber.length !== 10 || !/^\d+$/.test(mobileNumber))
                  }
                  placeholder="Mobile Number"
                  value={mobileNumber}
                  onChange={(ev) => setMobileNumber(ev.target.value)}
                />
                <FormFeedback>Please enter a valid Mobile number.</FormFeedback>
                <div className="d-block mt-2 pb-1 text-subtle">
                  OTP will be sent via WhatsApp
                </div>
              </FormGroup>

              <FormGroup className="mt-4">
                <select
                  className="form-select"
                  value={buildMode}
                  onChange={(e) => setBuildMode(e.target.value)}
                  style={{
                    color: buildMode === "" ? "#AAAAAA" : "",
                    fontWeight: buildMode === "" ? "normal" : "",
                    fontSize: "0.8rem",
                  }}
                >
                  <option value="">How do you build modular currently?</option>
                  <option value="self_cutlist">I / my team prepares cutlist</option>
                  <option value="factory_carpenter_cutlist">
                    Factory / Carpenter prepares cutlist
                  </option>
                  <option value="sub_contract">I sub-contract modular scope</option>
                  <option value="site_finish">I do site finish using a carpenter</option>
                </select>
              </FormGroup>

              <FormGroup className="mt-4">
                <select
                  className="form-select"
                  style={{
                    fontSize: "0.8rem",
                    color: monthlyOrders === "" ? "#AAAAAA" : "",
                  }}
                  value={monthlyOrders}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMonthlyOrders(value);

                    if (value === "lt2") setSize("s");
                    else if (value === "2to5") setSize("m");
                    else if (value === "gt5") setSize("l");
                    else setSize("");
                  }}
                >
                  <option value="">How many projects do you process (approx.)?</option>
                  <option value="lt2">&lt; 2 projects / month</option>
                  <option value="2to5">2 to 5 projects / month</option>
                  <option value="gt5">&gt; 5 projects / month</option>
                </select>
              </FormGroup>

              {isRegistered && (
                <Alert color="success">Registered Successfully</Alert>
              )}
              {error && <Alert color="danger">{error}</Alert>}

              <div className="d-grid gap-2 mt-4 mb-3">
                <AuthButton
                  color="primary"
                  onClick={triggerGetOtp}
                  disabled={!enableRegister || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" /> &nbsp;<span>Loading...</span>
                    </>
                  ) : (
                    "Continue"
                  )}
                </AuthButton>
              </div>

              <p className="text-center">
                Have already an account? &nbsp;
                <Link
                  to="/login_with_otp"
                  children="Login Here"
                  fontWeight="700"
                  color="black"
                />
              </p>
            </>
          )}

          {/* Step: Thank You */}
          {step === "thankyou" && (
            <div className="text-center m-2">
              <h4 className="mb-3 fw-bold fs-4">
                Thanks for your interest!
              </h4>
              <p className="mb-0 text-muted fs-6">
                {profile === "home_owner"
                  ? "Sorry, we are a B2B focussed company and cater to only businesses."
                  : profile === "carpenter"
                  ? "Sorry, our product doesn't work for carpenters yet."
                  : ""}
              </p>
            </div>
          )}
        </Form>
      </AuthContainer>
    </CoreContainer>
  );
}

export default Register;
